import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { store } from "./reduxSetup/store";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import process from 'process/browser.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorFallback from "./component/ErrorFallback";
import { CHUNK_RELOAD_KEY } from "./utils/chunkReloadGuard";
import { safeSessionStorage } from "./utils/safeStorage";
window.process = process;

function isStaleChunkError(error) {
  // Clean case: webpack's own chunk loader detects the network failure and
  // rejects with a proper ChunkLoadError / "Loading (CSS) chunk X failed".
  if (
    error?.name === "ChunkLoadError" ||
    /loading (css )?chunk [\w.-]+ failed/i.test(error?.message || "")
  ) {
    return true;
  }
  // Messy case: the host's static-file serving returns the SPA's
  // index.html (200 OK) instead of a real 404 for a stale/missing chunk
  // URL. The <script> tag's onload still fires (HTTP-level "success"), so
  // webpack's own ChunkLoadError path never triggers — the browser just
  // tries to parse that HTML as JavaScript and throws a generic top-level
  // SyntaxError (e.g. "Unexpected token 'else'" from stray markup/attribute
  // soup). A real syntax error in OUR shipped code can't reach production
  // (the build would have failed), so any runtime SyntaxError this late is
  // effectively always this masked-404 case.
  return error instanceof SyntaxError;
}

function handleAppError(error) {
  // A stale deploy leaves the browser holding chunk hashes that no longer
  // exist on the server. One forced reload picks up the fresh index.html
  // and manifest; the sessionStorage guard stops a repeat failure from
  // reload-looping the tab. This runs inside the ErrorBoundary's onError —
  // an unguarded storage throw here would compound the very crash it's
  // meant to recover from.
  if (isStaleChunkError(error) && !safeSessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    safeSessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
}

// The masked-404 SyntaxError case above is a raw parse-time error in an
// externally loaded <script> tag — it never goes through React at all, so
// Sentry.ErrorBoundary's onError (React render/commit errors only) can't
// see it. Needs its own top-level listener, registered before Sentry.init
// so nothing can load and fail before this is watching.
window.addEventListener("error", (event) => {
  handleAppError(event?.error);
});

const queryClient = new QueryClient()

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: process.env.NODE_ENV === "production",
  // Noise from in-app browsers (Instagram/Facebook) injecting their own
  // navigation-logging bridge script — not our code, nothing we can fix.
  ignoreErrors: [
    // Instagram/Facebook in-app browser's injected bridge script
    // (iabjs) calling into the native postMessage handler after the
    // host WebView tore it down (Android WKWebView-equivalent + iOS
    // WKWebView both do this on page-hide/backgrounding) — not our code.
    "Error invoking postMessage: Java object is gone",
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
  ],
  denyUrls: [
    /^iabjs:\/\//,
  ],
  // integrations: [
  //   Sentry.browserTracingIntegration(),
  //   Sentry.replayIntegration()
  // ],
  // Tracing
  // tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/, "https://admin.zayadryfruits.com/api", "http://192.168.1.23"], // Adjust this to your API URL
  // // Session Replay
  // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});


const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Sentry.ErrorBoundary fallback={ErrorFallback} onError={handleAppError}>
            <App />
          </Sentry.ErrorBoundary>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  </Provider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
